'use client';

import React from 'react';
import {
  TrendingHighlights,
  Props as TrendingHighlightsProps,
} from '@new-components/molecules/TrendingHighlights';

import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';

import Image from 'next/image';
import MuiContainer from '@new-components/atoms/MuiContainer';
import MuiBox from '@new-components/atoms/MuiBox';
import MuiTypography from '@new-components/atoms/MuiTypography';
import {ImgProps} from '@custom-types/ImgProps';
import Panel from '@new-components/molecules/Panel';
import { MediaQuery } from '@constants/theme';

type DestinationData = {
  id: string | number;
  name: string;
  tagLine: string;
  description: string;
  image: ImgProps;
  highlights: TrendingHighlightsProps['highlights'];
};
export type Props = {
  destinations: DestinationData[];
};
export function TrendingDestinations(props: Props) {
  const [activeTab, setActiveTab] = React.useState(props.destinations[0].id);
  const handleChange = (event: React.SyntheticEvent, newValue: string) =>
    setActiveTab(newValue);

  return (
    <>
      <MuiContainer
        sx={{marginBottom: '32px',
        padding: '0 24px',
        [MediaQuery.tablet]: {padding: '0 48px'}}}>
        <MuiTypography
          sx={{
            fontSize: '24px',
            fontWeight: 400,
            lineHeight: '32px',
            letterSpacing: '-0.4px',
            textAlign: 'left',
            textUnderlinePosition: 'from-font',
            textDecorationSkipInk: 'none',
            color: '#2D2D2D',
            marginBottom: '16px',
            [MediaQuery.desktop]: {
              fontSize: '32px',
              letterSpacing: '-0.8px',
              lineHeight: '35.2px',
            }
          }}>
          Trending Destination
        </MuiTypography>
        <MuiTypography
          sx={{
            fontSize: '15px',
            fontWeight: 300,
            lineHeight: '18.75px',
            letterSpacing: '-0.4000000059604645px',
            textAlign: 'left',
            textUnderlinePosition: 'from-font',
            textDecorationSkipInk: 'none',
            color: '#2D2D2D',
          }}>
            Discover the most popular destinations          
        </MuiTypography>
      </MuiContainer>
      <MuiBox sx={{position: 'relative', paddingTop: '24px', zIndex: 1}}>
        <MuiBox
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            height: '100%',
            width: '100%',
            background: '#2D2D2D',
            zIndex: -1,
          }}
        />
        <MuiBox
          sx={{
            background:
              'radial-gradient(74.08% 47.32% at 50% 52.8%, rgba(45, 45, 45, 0) 0%, #2D2D2D 100%)',
            position: 'relative',
          }}>
          <MuiBox
            sx={{
              position: 'sticky',
              top: {xs: 61, md: 73},
              backdropFilter: 'blur(80px)',
            }}>
            <MuiContainer>
              <Tabs
                value={activeTab}
                onChange={handleChange}
                allowScrollButtonsMobile
                scrollButtons
                variant='scrollable'
                TabScrollButtonProps={{sx: {color: '#ffffff'}}}
                sx={{
                  '.Mui-selected': {
                    color: '#ffffff !important',
                    opacity: '1 !important',
                  },
                }}
                TabIndicatorProps={{
                  style: {
                    backgroundColor: '#FFA629',
                  },
                }}>
                {props.destinations.map(({name: label, id: value}) => (
                  <Tab
                    sx={{color: '#ffffff', opacity: '0.6'}}
                    key={value}
                    label={label}
                    value={value}
                  />
                ))}
              </Tabs>
            </MuiContainer>
          </MuiBox>
          {props.destinations.map((data) => (
            <Panel activeTab={activeTab} value={data.id} key={data.id}>
              <MuiBox sx={{height: '60vh', img: {zIndex: -1}}}>
                <Image
                  layout='fill'
                  objectFit='cover'
                  src={data.image.src}
                  alt={data.image.alt}
                />
              </MuiBox>
              <MuiContainer>
                <MuiTypography
                  typography={'paragraph'}
                  sx={{
                    fontSize: '16px',
                    fontWeight: 500,
                    lineHeight: '22.4px',
                    letterSpacing: '-0.4px',
                    textAlign: 'left',
                    color: 'rgba(255, 255, 255, 0.6)',
                    textUnderlinePosition: 'from-font',
                    textDecorationSkipInk: 'none',
                  }}>
                  {data.tagLine}
                </MuiTypography>
                <MuiTypography
                  typography={'paragraph'}
                  sx={{
                    fontSize: '32px',
                    fontWeight: 300,
                    lineHeight: '44.8px',
                    letterSpacing: '-0.8px',
                    textAlign: 'left',
                    color: '#ffffff',
                    textUnderlinePosition: 'from-font',
                    textDecorationSkipInk: 'none',
                    paddingBlock: '24px 48px',
                  }}>
                  {data.description}
                </MuiTypography>
              </MuiContainer>
            </Panel>
          ))}
        </MuiBox>
        {props.destinations.map((data) => (
          <Panel activeTab={activeTab} value={data.id} key={data.id}>
            <TrendingHighlights highlights={data.highlights} />
          </Panel>
        ))}
      </MuiBox>
    </>
  );
}
